'use client'

/**
 * Purpose: Inline text link styled for secondary actions (not button-shaped CTAs).
 * Inputs: TextLinkProps — href for navigation or onClick for button mode.
 * Outputs: Renders Next.js Link or a native button with variant/size classes.
 */

import Link from 'next/link'

type TextLinkVariant = 'orange' | 'muted-on-dark' | 'danger' | 'muted'
type TextLinkSize = 'xs' | 'sm' | 'tagline'

// Visual presets — orange is the default accent link style.
const variantClasses: Record<TextLinkVariant, string> = {
  orange:
    'text-orange underline underline-offset-2 hover:opacity-80 transition-opacity',
  'muted-on-dark':
    'text-white/60 underline underline-offset-2 hover:text-white transition-colors',
  danger:
    'text-red-600 underline underline-offset-2 hover:text-red-800 transition-colors disabled:opacity-50',
  muted: 'text-black/50 hover:text-black transition-colors',
}

// Typography scale for inline links in forms and nav.
const sizeClasses: Record<TextLinkSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  tagline: 'navbar-brand-tagline mt-0',
}

function getVariantClass(variant: TextLinkVariant): string {
  switch (variant) {
    case 'orange':
      return variantClasses.orange
    case 'muted-on-dark':
      return variantClasses['muted-on-dark']
    // Danger and muted are destructive / low-emphasis affordances.
    case 'danger':
      return variantClasses.danger
    case 'muted':
      return variantClasses.muted
  }
}

// Size tokens keep typography aligned with navbar and form helper links.
function getSizeClass(size: TextLinkSize): string {
  switch (size) {
    case 'xs':
      return sizeClasses.xs
    case 'sm':
      return sizeClasses.sm
    case 'tagline':
      return sizeClasses.tagline
  }
}

// Supports navigation (href) or in-place actions (onClick) without button chrome.
interface TextLinkProps {
  children: React.ReactNode
  id?: string
  variant?: TextLinkVariant
  size?: TextLinkSize
  className?: string
  href?: string
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  target?: string
  rel?: string
}

/** Inline text link for secondary actions (not button-shaped). Use HoverGradientButton for CTAs. */
export default function TextLink({
  children,
  id = 'text-link',
  variant = 'orange',
  size = 'sm',
  className,
  href,
  onClick,
  type = 'button',
  disabled = false,
  target,
  rel,
}: TextLinkProps) {
  // Compose variant + size + optional caller overrides into one class string.
  const classes = `${getSizeClass(size)} ${getVariantClass(variant)}${className ? ` ${className}` : ''}`

  // href present → navigation link; otherwise render as a button for in-form actions.
  if (href) {
    return (
      <Link id={id} href={href} className={classes} target={target} rel={rel}>
        {children}
      </Link>
    )
  }

  return (
    <button id={id} type={type} onClick={onClick} disabled={disabled} className={classes}>
      {children}
    </button>
  )
}
